import { useRef, useState } from "react";
import { toast } from "sonner";
import { Radar, Copy, Check, RefreshCcw, Crosshair, Antenna, XCircle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { cancelJob, getDetections, pollJob } from "../lib/api";

const FORMATS = [
  { key: "sigma", label: "Sigma", body: "rule", lang: "yaml" },
  { key: "yara", label: "YARA", body: "rule", lang: "yara" },
  { key: "kql", label: "KQL", body: "query", lang: "kql" },
  { key: "splunk", label: "Splunk", body: "query", lang: "spl" },
  { key: "suricata", label: "Suricata", body: "rule", lang: "suricata" },
];

export default function DetectionRules({ cveId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jobInfo, setJobInfo] = useState(null);
  const [copied, setCopied] = useState(null);
  const cancelledRef = useRef(false);

  const run = async (force = false) => {
    setLoading(true);
    setJobInfo(null);
    cancelledRef.current = false;
    try {
      const { status, data: res } = await getDetections(cveId, force);

      // Sync path: cached result returned immediately.
      if (status === 200 && res.detections) {
        setData(res.detections);
        toast.success(
          force ? "Detections regenerated" : "Detection rules ready",
          { description: res.cached ? "Loaded from cache" : "Freshly generated" }
        );
        return;
      }

      // Async path: 202 with a job_id we have to poll.
      if (status === 202 && res.job_id) {
        setJobInfo({
          jobId: res.job_id,
          status: res.status,
          deduped: res.deduped,
        });
        if (res.deduped) {
          toast.info("Joined existing job", {
            description: "Another regenerate request is already running.",
          });
        }
        const job = await pollJob(res.job_id, { intervalMs: 2500, timeoutMs: 180000 });
        if (cancelledRef.current || job.status === "cancelled") {
          toast.info("Regenerate cancelled");
          return;
        }
        if (job.status === "done" && job.result?.detections) {
          setData(job.result.detections);
          setJobInfo(null);
          toast.success("Detection rules ready", {
            description: "Freshly generated",
          });
        } else if (job.status === "failed") {
          throw new Error(job.error || "Job failed");
        } else {
          throw new Error(`Unexpected job state: ${job.status}`);
        }
        return;
      }

      throw new Error("Unexpected response from server");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Generation failed";
      // 429 already surfaced by the axios interceptor.
      if (err?.response?.status !== 429 && !cancelledRef.current) {
        toast.error("Couldn't generate detections", { description: String(detail) });
      }
      setJobInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!jobInfo?.jobId) return;
    cancelledRef.current = true;
    try {
      await cancelJob(jobInfo.jobId);
      // The pollJob loop will surface a single "Regenerate cancelled" toast
      // when it observes status='cancelled'. Avoid double-toasting here.
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error("Couldn't cancel job", { description: String(detail) });
    }
  };

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(`${key.toUpperCase()} copied`);
      setTimeout(() => setCopied(null), 1400);
    } catch {
      toast.error("Clipboard blocked by browser");
    }
  };

  if (!data && !loading) {
    return (
      <section
        data-testid="detection-rules"
        className="panel p-6 md:p-7 fade-up delay-4"
      >
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="overline flex items-center gap-2">
              <Radar className="h-3 w-3 text-violet-700" />
              Detection rules · Sigma / YARA / KQL / Splunk / Suricata
            </div>
            <h3 className="font-display text-xl text-slate-900 mt-1">
              Generate SIEM &amp; EDR detection content
            </h3>
            <p className="text-slate-600 text-sm mt-2 max-w-2xl leading-relaxed">
              AI turns the patch diff and root-cause analysis into
              production-ready detection rules for your stack. Defensive
              only - no working exploit payloads embedded.
            </p>
          </div>
          <button
            onClick={() => run(false)}
            data-testid="generate-detections-btn"
            className="bg-slate-900 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
          >
            Generate detections
          </button>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section
        data-testid="detection-rules"
        className="panel p-6 md:p-7 fade-up delay-4 relative overflow-hidden"
      >
        <div className="scanner-line" />
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="overline">Generating detection rules…</div>
          <div className="flex items-center gap-2">
            {jobInfo?.jobId && (
              <span
                data-testid="job-id-badge"
                className="font-mono text-[10px] text-violet-700 border border-violet-200 bg-violet-50 px-2 py-0.5 rounded-sm"
              >
                JOB {jobInfo.jobId.slice(0, 10)}…
              </span>
            )}
            {jobInfo?.jobId && (
              <button
                onClick={cancel}
                data-testid="cancel-job-btn"
                className="flex items-center gap-1.5 border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 hover:text-red-800 rounded-md px-2.5 py-1 text-[11px] font-mono transition-colors"
              >
                <XCircle className="h-3 w-3" />
                Cancel
              </button>
            )}
          </div>
        </div>
        <div className="font-mono text-sm text-violet-700 space-y-1">
          <div>Compiling Sigma logsource taxonomy…</div>
          <div>Drafting YARA strings…</div>
          <div>Composing KQL across Sentinel tables…</div>
          <div>Writing Splunk SPL with eval/stats…</div>
          <div>Building Suricata signature…</div>
          <div>Mapping to MITRE ATT&amp;CK<span className="caret">&nbsp;</span></div>
        </div>
        <p className="text-[10px] font-mono text-slate-500 mt-4">
          Running as a background job - safe to keep the tab open. Average wait
          ~45–70s.
        </p>
      </section>
    );
  }

  const availFormats = FORMATS.filter((f) => data[f.key]?.available);
  const defaultTab = availFormats[0]?.key || "sigma";

  return (
    <section
      data-testid="detection-rules"
      className="panel p-6 md:p-7 fade-up delay-4"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="overline flex items-center gap-2">
            <Radar className="h-3 w-3 text-violet-700" />
            Detection rules · SOC-ready
          </div>
          <h3 className="font-display text-xl text-slate-900 mt-1">
            Drop straight into your SIEM
          </h3>
        </div>
        <button
          onClick={() => run(true)}
          data-testid="regenerate-detections-btn"
          className="flex items-center gap-1.5 border border-slate-300 rounded-md px-3 py-1.5 text-xs text-slate-700 hover:border-violet-300 hover:text-violet-700 transition-colors"
        >
          <RefreshCcw className="h-3 w-3" />
          Regenerate
        </button>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="bg-slate-50 border border-slate-200 rounded-md p-1 mb-4 flex flex-wrap">
          {FORMATS.map((f) => {
            const block = data[f.key];
            const enabled = block?.available;
            return (
              <TabsTrigger
                key={f.key}
                value={f.key}
                data-testid={`tab-${f.key}`}
                disabled={!enabled}
                className="font-mono text-xs data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm px-3 py-1.5 rounded-sm text-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {f.label}
                {!enabled && <span className="ml-1.5 text-[9px]">N/A</span>}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {FORMATS.map((f) => {
          const block = data[f.key] || {};
          return (
            <TabsContent key={f.key} value={f.key} className="mt-0">
              {!block.available ? (
                <div className="text-slate-500 text-sm p-4 bg-slate-50 border border-slate-200 rounded-md">
                  <span className="overline mr-2">Not applicable</span>
                  {block.reason_unavailable ||
                    "This format isn't a strong fit for this CVE class."}
                </div>
              ) : (
                <RuleBlock
                  format={f}
                  block={block}
                  copied={copied === f.key}
                  onCopy={() => copy(f.key, block[f.body] || "")}
                />
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
        {Array.isArray(data.ioc_summary) && data.ioc_summary.length > 0 && (
          <div
            className="bg-slate-50 border border-slate-200 rounded-md p-4"
            data-testid="ioc-summary"
          >
            <div className="overline flex items-center gap-2 mb-2">
              <Crosshair className="h-3 w-3 text-violet-700" />
              High-signal IoCs
            </div>
            <ul className="space-y-1">
              {data.ioc_summary.map((ioc, i) => (
                <li
                  key={i}
                  className="font-mono text-xs text-slate-700 break-all flex gap-2"
                >
                  <span className="text-violet-700 shrink-0">•</span>
                  <code>{ioc}</code>
                </li>
              ))}
            </ul>
          </div>
        )}

        {Array.isArray(data.telemetry_required) &&
          data.telemetry_required.length > 0 && (
            <div
              className="bg-slate-50 border border-slate-200 rounded-md p-4"
              data-testid="telemetry-required"
            >
              <div className="overline flex items-center gap-2 mb-2">
                <Antenna className="h-3 w-3 text-violet-700" />
                Telemetry required
              </div>
              <ul className="space-y-1">
                {data.telemetry_required.map((t, i) => (
                  <li key={i} className="text-xs text-slate-700 flex gap-2">
                    <span className="text-violet-700 shrink-0">•</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
      </div>

      {Array.isArray(data.attack_techniques) &&
        data.attack_techniques.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="attack-techniques">
            <span className="overline mr-1">MITRE ATT&amp;CK</span>
            {renderAttackChips(data.attack_techniques)}
          </div>
        )}
    </section>
  );
}

function RuleBlock({ format, block, copied, onCopy }) {
  const code = block[format.body] || "";
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50">
        <span className="text-[10px] font-mono uppercase tracking-wider text-violet-700 border border-violet-200 bg-violet-50 px-1.5 py-0.5 rounded-sm">
          {format.lang}
        </span>
        <span className="font-mono text-xs text-slate-700 truncate flex-1">
          {block.title || `${format.label} rule`}
        </span>
        <button
          onClick={onCopy}
          data-testid={`copy-${format.key}`}
          className="flex items-center gap-1.5 text-xs text-slate-700 hover:text-violet-700 transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-3.5 w-3.5 text-emerald-700" />
              <span className="text-emerald-700">Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3.5 w-3.5" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <pre className="font-mono text-xs leading-relaxed p-4 text-slate-800 whitespace-pre overflow-x-auto max-h-[460px]">
        {code}
      </pre>
      {block.tuning_note && (
        <div className="border-t border-slate-100 px-4 py-2.5 bg-slate-50">
          <span className="overline mr-2">Tuning</span>
          <span className="text-xs text-slate-600">{block.tuning_note}</span>
        </div>
      )}
    </div>
  );
}


// MITRE ATT&CK technique-ID grammar (Enterprise/Mobile/ICS):
//   Tnnnn         (main technique)         e.g. T1190
//   Tnnnn.nnn     (sub-technique)          e.g. T1059.001
const ATTACK_ID_RE = /^T\d{4}(?:\.\d{3})?$/;

function attackUrl(id) {
  // Sub-techniques live at /techniques/<main>/<sub>/.  Use split() so we
  // never accidentally swap more than one delimiter.
  const [main, sub] = id.split(".");
  return sub
    ? `https://attack.mitre.org/techniques/${main}/${sub}/`
    : `https://attack.mitre.org/techniques/${main}/`;
}

function renderAttackChips(items) {
  const seen = new Set();
  const chips = [];
  for (const t of items) {
    if (!t || typeof t !== "object") continue;
    const id = String(t.id || "").trim().toUpperCase();
    const name = String(t.name || "").trim();
    if (!id || !name) continue;
    if (!ATTACK_ID_RE.test(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    chips.push(
      <a
        key={id}
        href={attackUrl(id)}
        target="_blank"
        rel="noreferrer"
        data-testid={`attack-chip-${id}`}
        className="text-[11px] font-mono text-violet-700 border border-violet-200 bg-violet-50 hover:bg-violet-100 rounded-sm px-2 py-0.5 transition-colors"
      >
        {id} . {name}
      </a>
    );
    if (chips.length >= 12) break;
  }
  return chips;
}
